/**
 * Admin Measurements State
 *
 * Loads and caches all measurements for students who have active goals across
 * the team. Batches Firestore queries in groups of 10 (Firestore's `in` limit)
 * using the unique student IDs derived from AdminGoalsState.
 *
 * Exposes a `measurementIndex` computed signal that is ready to pass directly
 * into `augmentGoal()` so that on-track / behind status is computed correctly.
 *
 * Admin-only — initialized by StateManagerService after AdminGoalsState.
 * No real-time listener: measurements are read for admin analysis, not live editing.
 *
 * Measurements stored at: partners/{partnerId}/measurements
 *
 * @example
 * await adminObservationsState.initialize({
 *   activeTeamId: 'team-123',
 *   studentIds: goalStudentIds,
 * });
 *
 * // Use in a component computed signal:
 * augmentedGoal = computed(() =>
 *   augmentGoal(rawGoal, adminObservationsState.measurementIndex())
 * );
 */

import { Injectable, inject, computed, signal } from '@angular/core';
import { FirestoreService } from 'src/app/core/services/firestore/firestore';
import { StateCacheService } from 'src/app/core/services/state-cache.service';
import { Observation } from 'src/app/domain/observations/types/observations.types';
import {
  buildMeasurementIndex,
  type MeasurementIndex,
} from 'src/app/domain/goals/services/goal-augmentation';
import {
  daysAgoMs,
  earliestObservationByPair,
  latestNObservationsByPair,
  latestObservationByPair,
  latestObservationTimestampByStudent,
  observationTimestamp,
  RECENT_WINDOW_DAYS,
  type PairKey,
} from 'src/app/domain/analytics';
import {
  AdminObservationsInitConfig,
  ADMIN_OBSERVATIONS_DEFAULTS,
} from './admin-observations.types';

/** Firestore `in` operator maximum clause size. */
const FIRESTORE_IN_LIMIT = 10;

@Injectable({
  providedIn: 'root',
})
export class AdminObservationsState {
  // =============================================
  // DEPENDENCIES
  // =============================================
  private readonly firestoreService = inject(FirestoreService);
  private readonly stateCacheService = inject(StateCacheService);

  // =============================================
  // PRIVATE STATE (Writable Signals)
  // =============================================
  private readonly _data            = signal<Observation[]>(ADMIN_OBSERVATIONS_DEFAULTS.data);
  private readonly _initialized     = signal<boolean>(ADMIN_OBSERVATIONS_DEFAULTS.initialized);
  private readonly _loading         = signal<boolean>(ADMIN_OBSERVATIONS_DEFAULTS.loading);
  private readonly _error           = signal<string | null>(ADMIN_OBSERVATIONS_DEFAULTS.error);
  private readonly _lastUpdated     = signal<number | null>(ADMIN_OBSERVATIONS_DEFAULTS.lastUpdated);
  private readonly _lastSync        = signal<number | null>(ADMIN_OBSERVATIONS_DEFAULTS.lastSync);
  private readonly _hydratedFromCache = signal<boolean>(ADMIN_OBSERVATIONS_DEFAULTS.hydratedFromCache);
  private readonly _activeTeamId    = signal<string | null>(ADMIN_OBSERVATIONS_DEFAULTS.activeTeamId);
  private readonly _studentIds      = signal<string[]>(ADMIN_OBSERVATIONS_DEFAULTS.studentIds);

  // =============================================
  // PUBLIC STATE (Readonly Signals)
  // =============================================
  readonly data             = this._data.asReadonly();
  readonly initialized      = this._initialized.asReadonly();
  readonly loading          = this._loading.asReadonly();
  readonly error            = this._error.asReadonly();
  readonly lastUpdated      = this._lastUpdated.asReadonly();
  readonly lastSync         = this._lastSync.asReadonly();
  readonly hydratedFromCache = this._hydratedFromCache.asReadonly();
  readonly activeTeamId     = this._activeTeamId.asReadonly();
  readonly studentIds       = this._studentIds.asReadonly();

  // =============================================
  // COMPUTED STATE (Derived Values)
  // =============================================

  readonly isReady = computed(
    () => this.initialized() && !this.loading() && !this.error()
  );

  readonly hasData = computed(() => this.data().length > 0);

  readonly count = computed(() => this.data().length);

  readonly studentCount = computed(() => this._studentIds().length);

  /**
   * Pre-built MeasurementIndex keyed by `studentId::skillId::type`.
   * Pass this directly into augmentGoal() to get on-track / behind status.
   * Recomputed whenever the underlying measurement data changes.
   */
  readonly measurementIndex = computed((): MeasurementIndex =>
    buildMeasurementIndex(this._data())
  );

  // ──────────────────────────────────────────────────────────────────
  // Observation-aggregate signals (hoisted from per-widget computed())
  //
  // Each of these used to be computed independently inside every
  // consuming widget; centralizing them on the store collapses N walks
  // of the observation list per state change down to 1. Angular's
  // signal dependency tracking handles invalidation — when `_data`
  // changes, each signal recomputes exactly once.
  // ──────────────────────────────────────────────────────────────────

  /** `(studentId, skillId) → most recent observation` for the team. */
  readonly latestByPair = computed(() => latestObservationByPair(this._data()));

  /** `(studentId, skillId) → earliest observation` for the team. */
  readonly earliestByPair = computed(() => earliestObservationByPair(this._data()));

  /** `studentId → most recent observation timestamp` across every skill. */
  readonly latestTimestampByStudent = computed(() =>
    latestObservationTimestampByStudent(this._data()),
  );

  /**
   * `(studentId, skillId) → two most recent observations, newest first`.
   * Used by regression-watch to compare latest vs prior; widen with an
   * additional signal if a third consumer needs a different `n`.
   */
  readonly latestTwoByPair = computed<Map<PairKey, Observation[]>>(() =>
    latestNObservationsByPair(this._data(), 2),
  );

  /**
   * Observations inside the trailing `RECENT_WINDOW_DAYS` window
   * (canonical 30 days). Every widget that filters observations by
   * "recent" should bind to this signal rather than rebuilding its own
   * `data().filter(obs => observationTimestamp(obs) >= cutoff)` inside
   * a local `computed()`. The cutoff is captured per recompute, so the
   * filter result re-evaluates only when the underlying observation
   * list changes.
   */
  readonly recentObservations = computed(() => {
    const cutoff = daysAgoMs(RECENT_WINDOW_DAYS);
    return this._data().filter(obs => {
      const ts = observationTimestamp(obs);
      return ts !== null && ts >= cutoff;
    });
  });

  // =============================================
  // PRIVATE
  // =============================================
  private _isInitializing = false;

  // =============================================
  // LIFECYCLE & INITIALIZATION
  // =============================================

  async initialize(config: AdminObservationsInitConfig): Promise<void> {
    if (this._isInitializing) {
      console.warn('[AdminObservationsState] Initialization already in progress');
      return;
    }

    if (config.studentIds.length === 0) {
      this._initialized.set(true);
      this._loading.set(false);
      return;
    }

    this._isInitializing = true;
    this._loading.set(true);
    this._error.set(null);
    this._activeTeamId.set(config.activeTeamId);
    this._studentIds.set(config.studentIds);

    const cacheKey = `blume_state_cache_measurements_${config.activeTeamId}`;

    try {
      // STEP 1: Try to hydrate from localStorage
      const cached = this.stateCacheService.loadStateCache<Observation[]>(
        'measurements',
        config.activeTeamId
      );

      if (cached) {
        const cacheAgeMs   = Date.now() - cached.cachedAt;
        const cacheAgeMins = Math.round(cacheAgeMs / 60000);
        this._data.set(cached.data);
        this._lastSync.set(cached.lastSync);
        this._hydratedFromCache.set(true);
        this._initialized.set(true);
        this._loading.set(false); // Show cached data immediately
      } else {
      }

      // STEP 2: Full load from Firestore
      // We do a full load every time (no incremental updatedAt filter) to avoid
      // requiring a composite index. The cache is served instantly; this refresh
      // runs in the background after cached data is already in use.
      const updates = await this.loadMeasurements(
        config.activeTeamId,
        config.studentIds
      );

      // STEP 3: Merge or replace
      if (cached && updates.length > 0) {
        this.mergeMeasurements(updates);
      } else if (!cached) {
        this._data.set(updates);
      } else {
      }

      // STEP 4: Update metadata
      this._initialized.set(true);
      const now = Date.now();
      this._lastUpdated.set(now);
      this._lastSync.set(now);

      // STEP 5: Save to localStorage
      this.stateCacheService.saveStateCache(
        'measurements',
        config.activeTeamId,
        this._data(),
        now
      );

    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to initialize';
      this._error.set(errorMessage);
      console.error('[AdminObservationsState] ❌ Initialization failed:', error);
      throw error;
    } finally {
      this._loading.set(false);
      this._isInitializing = false;
    }
  }

  reset(): void {
    const activeTeamId = this._activeTeamId();
    if (activeTeamId) {
      this.stateCacheService.clearStateCache('measurements', activeTeamId);
    }

    this._data.set(ADMIN_OBSERVATIONS_DEFAULTS.data);
    this._initialized.set(ADMIN_OBSERVATIONS_DEFAULTS.initialized);
    this._loading.set(ADMIN_OBSERVATIONS_DEFAULTS.loading);
    this._error.set(ADMIN_OBSERVATIONS_DEFAULTS.error);
    this._lastUpdated.set(ADMIN_OBSERVATIONS_DEFAULTS.lastUpdated);
    this._lastSync.set(ADMIN_OBSERVATIONS_DEFAULTS.lastSync);
    this._hydratedFromCache.set(ADMIN_OBSERVATIONS_DEFAULTS.hydratedFromCache);
    this._activeTeamId.set(ADMIN_OBSERVATIONS_DEFAULTS.activeTeamId);
    this._studentIds.set(ADMIN_OBSERVATIONS_DEFAULTS.studentIds);
    this._isInitializing = false;

  }

  async forceRefresh(): Promise<void> {
    const teamId    = this._activeTeamId();
    const studentIds = this._studentIds();

    if (!teamId) {
      console.warn('[AdminObservationsState] Cannot force refresh — no active team');
      return;
    }

    this.stateCacheService.clearStateCache('measurements', teamId);
    this._lastSync.set(null);
    this._hydratedFromCache.set(false);

    await this.initialize({ activeTeamId: teamId, studentIds });
  }

  clearError(): void {
    this._error.set(null);
  }

  // =============================================
  // PRIVATE METHODS (Data Loading)
  // =============================================

  /**
   * Batch-loads all measurements for the given student IDs from Firestore.
   * Firestore's `in` operator is limited to 10 values per query, so the IDs
   * are chunked and queried in parallel.
   */
  private async loadMeasurements(
    activeTeamId: string,
    studentIds: string[]
  ): Promise<Observation[]> {
    const collectionPath = `partners/${activeTeamId}/observations`;
    const batches = this.chunkArray(studentIds, FIRESTORE_IN_LIMIT);


    try {
      // Run all batch queries in parallel for speed
      const batchResults = await Promise.all(
        batches.map(batch =>
          this.firestoreService.getCollectionAsync<Observation>(
            collectionPath,
            { where: [{ field: 'studentId', operator: 'in', value: batch }] }
          )
        )
      );

      const allMeasurements = batchResults.flat();


      return allMeasurements;
    } catch (error) {
      console.error('[AdminObservationsState] Failed to load measurements:', error);
      throw error;
    }
  }

  /**
   * Merges new/updated measurements into the existing set.
   * Uses measurement.id as the deduplication key.
   */
  private mergeMeasurements(newMeasurements: Observation[]): void {
    const currentData  = this._data();
    const activeTeamId = this._activeTeamId();

    const dataMap = new Map(currentData.map(m => [m.id, m]));
    newMeasurements.forEach(m => {
      if (m.id) dataMap.set(m.id, m);
    });

    const merged = Array.from(dataMap.values());
    this._data.set(merged);

    const now = Date.now();
    this._lastUpdated.set(now);
    this._lastSync.set(now);

    if (activeTeamId) {
      this.stateCacheService.saveStateCache('measurements', activeTeamId, merged, now);
    }

  }

  // =============================================
  // PRIVATE HELPERS
  // =============================================

  private chunkArray<T>(arr: T[], size: number): T[][] {
    const chunks: T[][] = [];
    for (let i = 0; i < arr.length; i += size) {
      chunks.push(arr.slice(i, i + size));
    }
    return chunks;
  }
}
